import type { FC } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Transition } from '@headlessui/react'
import { BubbleMenu } from '@tiptap/react'
import { first } from 'lodash'

import IconClose from '@/assets/icons/icon-close.svg'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tooltip } from '@/components/ui/tooltip'
import IconRefresh from '@/modules/chatting/icons/icon-refresh.svg'
import IconTextBold from '@/modules/chatting/icons/icon-text-bold.svg'
import IconTextColor from '@/modules/chatting/icons/icon-text-color.svg'
import IconTextItalic from '@/modules/chatting/icons/icon-text-italic.svg'
import { classNames } from '@/utils/helpers'

import { IconButton } from '../../components/icon-button'
import {
  CLASSNAME_PATTERN_COLOR,
  CLASSNAME_PATTERN_SIZE,
  COLORS_SCHEME,
  FONT_COLORS_LIST,
  FONT_SIZES_LIST,
  SIZES_SCHEME,
} from '../constants'
import { useEditor } from './use-editor'
import { hasTextStyle } from './utils'

const appendTo = () => document.getElementById('root')!

interface Props {
  isVisible: boolean
  onClose: () => void
}

export const MessageFormatter: FC<Props> = ({ isVisible, onClose }) => {
  const [currentSize, setCurrentSize] = useState<string>(SIZES_SCHEME.DEFAULT)
  const [currentColor, setCurrentColor] = useState<string>(
    COLORS_SCHEME.DEFAULT,
  )
  const [isBold, setIsBold] = useState<boolean>(false)
  const [isItalic, setIsItalic] = useState<boolean>(false)

  const { editor } = useEditor()

  const toggleBold = useCallback(() => {
    if (editor?.isInitialized) {
      editor.chain().focus().toggleBold().run()
      setIsBold((prev) => !prev)
    }
  }, [editor])

  const toggleItalic = useCallback(() => {
    if (editor?.isInitialized) {
      editor.chain().focus().toggleItalic().run()
      setIsItalic((prev) => !prev)
    }
  }, [editor])

  const toggleColor = useCallback(
    (color: string) => {
      if (editor?.isInitialized) {
        editor
          .chain()
          .focus()
          .reassignClasses({
            classes: [color, currentSize],
            removeClassNamePatterns: [
              CLASSNAME_PATTERN_COLOR,
              CLASSNAME_PATTERN_SIZE,
            ],
          })
          .run()
        setCurrentColor(color)
        editor.commands.focus()
      }
    },
    [editor, currentSize],
  )

  const toggleSize = useCallback(
    (sizeClassName: string) => {
      if (editor?.isInitialized) {
        editor
          .chain()
          .focus()
          .reassignClasses({
            classes: [sizeClassName, currentColor],
            removeClassNamePatterns: [
              CLASSNAME_PATTERN_SIZE,
              CLASSNAME_PATTERN_COLOR,
            ],
          })
          .run()
        setCurrentSize(sizeClassName)

        // Focus the editor at the end of the document with small delay to avoid Select focus
        setTimeout(() => {
          editor.commands.focus('end')
        }, 0)
      }
    },
    [editor, currentColor],
  )

  useEffect(() => {
    if (!editor?.isInitialized) {
      return
    }

    const updateStateFromEditor = () => {
      const matchingSizes = FONT_SIZES_LIST.filter((item) =>
        hasTextStyle(editor.state, item.value),
      )
      const matchingSize = first(matchingSizes)?.value

      // If there is a matching size, set the current size to the matching size
      if (matchingSize) {
        setCurrentSize(matchingSize)
      } else {
        // If there is no matching size, set the current size to the default size
        setCurrentSize(SIZES_SCHEME.DEFAULT)
      }

      const matchingColors = FONT_COLORS_LIST.filter((item) =>
        item.value ? hasTextStyle(editor.state, item.value) : false,
      )
      const matchingColor = first(matchingColors)?.value

      if (matchingColor) {
        setCurrentColor(matchingColor)
      } else {
        setCurrentColor(COLORS_SCHEME.DEFAULT)
      }

      setIsBold(editor.isActive('bold'))
      setIsItalic(editor.isActive('italic'))
    }

    // Attach event listener to track state updates
    editor.on('selectionUpdate', updateStateFromEditor)
    editor.on('transaction', updateStateFromEditor)
    editor.on('update', updateStateFromEditor)

    return () => {
      editor.off('selectionUpdate', updateStateFromEditor)
      editor.off('transaction', updateStateFromEditor)
      editor.off('update', updateStateFromEditor)
    }
  }, [editor])

  const clearFormatting = useCallback(() => {
    if (editor?.isInitialized) {
      // Clear formatting for both selected text and for future typing
      editor.commands.clearNodes()
      editor.commands.unsetAllMarks()

      // Unset individual styles (explicitly unset bold and italic)
      if (editor.isActive('bold')) {
        editor.commands.unsetBold()
      }
      if (editor.isActive('italic')) {
        editor.commands.unsetItalic()
      }

      // Explicitly unset text styles
      editor.chain().focus().setMark('textStyle', { class: '' }).run()

      // Reset internal state
      setIsBold(false)
      setIsItalic(false)
      setCurrentColor(COLORS_SCHEME.DEFAULT)
      setCurrentSize(SIZES_SCHEME.DEFAULT)

      // Refocus the editor for further typing
      editor.commands.focus()
    }
  }, [editor])

  const renderColors = useMemo(() => {
    return FONT_COLORS_LIST.map(({ order, value, isDivider }) =>
      isDivider ? (
        <span
          key={order}
          className="h-6 w-[2px] bg-secondary-300"
        />
      ) : (
        <div
          key={order}
          onClick={() => value && toggleColor(value)}
        >
          <button
            className={classNames('h-6 w-6 rounded-full', value, {
              'ring-2 ring-primary-500': currentColor === value,
            })}
          />
        </div>
      ),
    )
  }, [toggleColor, currentColor])

  return (
    <>
      {editor?.isInitialized ? (
        <BubbleMenu
          editor={editor}
          tippyOptions={{
            duration: 100,
            placement: 'bottom',
            appendTo,
          }}
        >
          <div className="flex gap-3 rounded-md border bg-card p-2 shadow-md">
            <button
              onClick={toggleBold}
              className={isBold ? 'font-bold underline' : ''}
            >
              Bold
            </button>
            <button
              onClick={toggleItalic}
              className={isItalic ? 'font-bold underline' : ''}
            >
              Italic
            </button>
            <div className="flex flex-row gap-2 text-text-default">
              {renderColors}
            </div>
          </div>
        </BubbleMenu>
      ) : null}
      <Transition
        show={isVisible}
        enter="transition-all duration-100 ease-out"
        enterFrom="opacity-0 "
        enterTo="opacity-100 "
        leave="transition-all duration-100 ease-in"
        leaveFrom="opacity-100 "
        leaveTo="opacity-0 "
      >
        <div className="flex w-full flex-row items-center gap-3 pt-4">
          <Select
            value={String(currentSize)}
            onValueChange={toggleSize}
          >
            <SelectTrigger className="w-[150px] justify-center gap-1 text-base text-secondary-500 [&>div]:flex-none">
              <Tooltip content="Font size">
                <SelectValue />
              </Tooltip>
            </SelectTrigger>
            <SelectContent className="max-h-[254px] gap-2">
              {FONT_SIZES_LIST.map(({ textKey, value }) => (
                <SelectItem
                  className={classNames('justify-center', value)}
                  key={value}
                  value={value}
                >
                  {textKey}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <IconButton
            className="h-5 w-5"
            icon={IconTextItalic}
            onClick={toggleItalic}
            tooltip="Italic"
          />
          <IconButton
            className="h-5 w-5"
            icon={IconTextBold}
            onClick={toggleBold}
            tooltip="Bold"
          />
          <Popover>
            <PopoverTrigger
              className="h-5 w-5"
              asChild
            >
              <IconButton
                tooltip="Color"
                className="h-5 w-5"
                icon={IconTextColor}
              />
            </PopoverTrigger>
            <PopoverContent>
              <div className="flex flex-row gap-2 text-text-default">
                {renderColors}
              </div>
            </PopoverContent>
          </Popover>
          <span className="h-6 w-[2px] bg-secondary-300" />
          <IconButton
            className="h-5 w-5"
            icon={IconRefresh}
            onClick={clearFormatting}
            tooltip="Clear formatting"
          />
          <div className="ml-auto">
            <IconButton
              className="ml-auto h-5 w-5"
              icon={IconClose}
              onClick={onClose}
              tooltip="Close Formatting Panel"
            />
          </div>
        </div>
      </Transition>
    </>
  )
}
